// External Dependencies
import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import Spinner from 'sushiweb/Spinner/Spinner';

// Internal Dependencies
import { useQueryCall, useMutationCall } from '../../../api/react-client';
import { EmptyState } from '../../commons';
import { InvalidSearchImg, infoIcon } from '../../../images/images';
import Shimmer from 'sushiweb/Shimmer';
import { Info } from 'sushiweb/Icons';

// UI Components
import { Dropdown, Heading, Textarea, Tooltip } from '../../../UiKit';

// multiSelect styles
const multiSelectStyles = `margin: 1rem;
                          .option:hover {
                                background-color: #318616 !important;
                           }
                          `;

// Form field info tooltip component
const getHeaderWithTooltip = (title, tip) => {
  return (
    <div className='tw-flex tw-items-center tw-gap-2'>
      <Heading title={title} />
      {tip && (
        <span style={{ marginBottom: '0.5rem' }}>
          <Tooltip
            position='bottom'
            border={false}
            title={
              <p>
                <span className='icon' style={{ marginLeft: '0.5rem' }}>
                  <img src={infoIcon} alt='' />
                </span>
              </p>
            }
            label={tip}
          />
        </span>
      )}
    </div>
  );
};

/**
 * Modal component for marking users as fraud
 * @component
 * @param {Object} props - Component props
 * @param {string} props.orderId - Order ID to mark as fraud
 * @param {Function} props.closePopup - Function to close the modal
 */
export const MarkFraudModal = ({ orderId, userId, closePopup }) => {
  // State for primary reasons management
  const [primaryReasonList, setPrimaryReasonList] = useState([]);
  const [primaryReasons, setPrimaryReasons] = useState([]);

  // State for secondary reasons management
  const [secondaryReasonList, setSecondaryReasonList] = useState([]);
  const [secondaryReasons, setSecondaryReasons] = useState([]);

  // State for expiry and comments
  const [expiryOptions, setExpiryOptions] = useState([]);
  const [expiryDays, setExpiryDays] = useState('');
  const [comment, setComment] = useState('');

  // state for entity types
  const [entityTypesList, setEntityTypesList] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  const multiselectRef = useRef();

  // Fetch fraud parameters using react-query
  const markFraudEndPoint = 'user/mark_fraud';

  const {
    data: fraudParams,
    error: paramsError,
    isLoading: isParamsLoading,
    isError: isParamsError,
  } = useQueryCall(
    {
      onSuccess: (response) => {
        // Check for nested data structure
        if (!response?.reason_options) {
          toast.error('Invalid fraud parameters received');
          return;
        }

        // Transform primary reasons into correct format for Multiselect
        const primaryOptions = Object.keys(response.reason_options).map(
          (reason, index) => ({
            id: index,
            name: reason,
          }),
        );
        setPrimaryReasonList(primaryOptions);

        // Transform and set expiry options
        if (response.karma_expiry_options?.length) {
          setExpiryOptions(response.karma_expiry_options);
        }

        // Transform entity types into correct format for Multiselect
        const entityOptions = response.entity_types?.map((entity_type) => ({
          id: entity_type?.value,
          name: entity_type?.label,
        }));
        setEntityTypesList(entityOptions);
      },
    },
    markFraudEndPoint,
    ['getFraudParams', orderId],
  );

  // Memoize transformed options to prevent unnecessary re-renders
  const memoizedExpiryOptions = useMemo(() => expiryOptions, [expiryOptions]);

  const { mutate: markFraud, isLoading: isMarking } = useMutationCall(
    {
      onSuccess: (data) => {
        if (data?.success) {
          toast.success(
            data.message ? data.message : 'Marked as fraud successfully',
          );
          closePopup();
        }
      },
    },
    markFraudEndPoint,
    [], // Not cached so that we can retry marking fraud
    {
      doNotShowSuccessToast: true,
    },
  );

  if (isParamsLoading) return <Shimmer height='20rem' width={'100%'} />;

  if (isParamsError) {
    return (
      <div className='tw-w-full'>
        <EmptyState
          img={InvalidSearchImg}
          heading={paramsError?.message || 'Something went wrong!'}
          isPop={true}
        />
        ;
      </div>
    );
  }

  const refreshEligibleSecondaryReasons = (selectedList) => {
    // Get secondary reasons for all selected primary reasons
    const secondaryReasons = selectedList.flatMap(
      (item) => fraudParams.reason_options[item.name] || [],
    );

    // Update secondary reasons list
    setSecondaryReasonList(
      secondaryReasons.map((reason, index) => ({
        id: index,
        name: reason,
      })),
    );

    // Clear previous secondary selections
    setSecondaryReasons([]);
  };

  const handleSecondaryReasonUpdate = (selectedList) => {
    // Update secondary reasons state with the new selected list
    setSecondaryReasons(selectedList);
  };

  const handleEntityTypeUpdate = (selectedList) => {
    // Update entity types state with the new selected list
    setEntityTypes(selectedList);
  };

  const handlePrimaryReasonUpdate = (selectedList) => {
    // Update primary reasons state with the new selected list
    setPrimaryReasons(selectedList);

    // Refresh secondary reasons based on new selection
    refreshEligibleSecondaryReasons(selectedList);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!secondaryReasons.length) {
      toast.error('Select at least one specific reason');
    } else if (expiryDays == '') {
      toast.error('Select expiry days');
    } else if (!entityTypes?.length) {
      toast.error('Select at least one entity type');
    } else {
      const dataToSubmit = {
        order_id: parseInt(orderId, 10) || 0,
        customer_id: parseInt(userId, 10) || 0,
        reason_codes: secondaryReasons?.map((reason) => reason?.name),
        comment: comment,
        expiry_days: memoizedExpiryOptions[expiryDays].value,
        entity_types: entityTypes?.map((entity) => entity?.id) || [],
      };
      markFraud(dataToSubmit);
    }
  };

  return (
    <div className='tw-px-4'>
      <div className='tw-px-4 tw-p-3 tw-bg-yellow-200 tw-rounded-lg tw-flex tw-flex-col tw-gap-1'>
        <div className='tw-text-200 tw-font-medium tw-text-grey-800 tw-flex tw-gap-1 tw-justify-start'>
          <Info size={12} />
          <span className='tw-text-300'>
            {'Select all reasons behind marking customer as fraud'}
          </span>
        </div>
      </div>

      {fraudParams && (
        <form onSubmit={handleSubmit}>
          {/* Entity Types Selection Dropdown */}
          {entityTypesList?.length > 0 ? (
            <label htmlFor='entityTypes' css={multiSelectStyles}>
              {getHeaderWithTooltip(
                'Entity Type',
                'Select the type of entity you want to mark as fraud',
              )}
              <Multiselect
                ref={multiselectRef}
                avoidHighlightFirstOption
                options={entityTypesList || []}
                selectedValues={entityTypes}
                onSelect={handleEntityTypeUpdate}
                onRemove={handleEntityTypeUpdate}
                displayValue='name'
                placeholder='Search entity types...'
              />
            </label>
          ) : null}

          {/* Primary and Secondary Reasons */}
          <label htmlFor='primaryReasons' css={multiSelectStyles}>
            {getHeaderWithTooltip('Base Reasons')}
            <Multiselect
              ref={multiselectRef}
              avoidHighlightFirstOption
              options={primaryReasonList}
              selectedValues={primaryReasons}
              onSelect={handlePrimaryReasonUpdate}
              onRemove={handlePrimaryReasonUpdate}
              displayValue='name'
              placeholder='Search reasons...'
            />
          </label>

          <label htmlFor='secondaryReasons' css={multiSelectStyles}>
            {getHeaderWithTooltip(
              'Specific Reasons',
              'Select exact reasons corresponding to the base reason(s) selected',
            )}
            <Multiselect
              ref={multiselectRef}
              avoidHighlightFirstOption
              options={secondaryReasonList}
              selectedValues={secondaryReasons}
              onSelect={handleSecondaryReasonUpdate}
              onRemove={handleSecondaryReasonUpdate}
              displayValue='name'
              placeholder='Search reasons...'
            />
          </label>

          {/* Expiry Days */}
          <label htmlFor='expiryDays' css={multiSelectStyles}>
            {getHeaderWithTooltip(
              'Expiry Days',
              'For how many days should this customer be marked as fraud?',
            )}
            <Dropdown
              dropDownValues={memoizedExpiryOptions.map((opt) =>
                opt.label.toString(),
              )}
              onSelect={(value) => {
                setExpiryDays(value);
              }}
              dropdownVal={expiryDays.toString()}
              placeholder='Please Select Expiry Days'
            />
          </label>

          {/* Comments Section */}
          <label
            htmlFor='Comment'
            css={`
              margin-top: 2rem;
            `}
          >
            {getHeaderWithTooltip(
              'Remarks',
              'Add any extra information that helped identify fraudulent behavior',
            )}
            <Textarea
              placeholder='Add some remarks'
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
              maxLimit='300'
              showBtn={false}
            />
          </label>

          {/* Submit Button */}
          <div
            css={`
              margin-top: 3rem;
              display: flex;
              justify-content: flex-end;
            `}
          >
            <button
              type='submit'
              disabled={isMarking || isParamsLoading}
              className='tw-text-[14px] tw-font-medium tw-bg-[#318616] tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-12 tw-py-4 tw-min-h-8 tw-leading-none tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-[#cfcfcf]'
            >
              {isMarking ? (
                <div className='tw-w-full tw-justify-center tw-items-center tw-text-white'>
                  <Spinner color={'white'} />
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Update PropTypes to accept both string and number
MarkFraudModal.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closePopup: PropTypes.func.isRequired,
};

MarkFraudModal.defaultProps = {
  orderId: 0,
  userId: 0,
  closePopup: () => {},
};
